/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx, css } from '@emotion/react'
import { Fragment, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  EuiCheckbox,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
  EuiToolTip,
  useEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { createDeploymentUrl } from '@modules/cloud-api/v1/urls'
import type { DeploymentCreateRequest, DeploymentCreateResponse } from '@modules/cloud-api/v1/types'
import { getResponseStatus } from '@modules/utils/ajax'
import { AjaxRequestError } from '@modules/ui-types'
import { getUserUsecase } from '@modules/profile-lib'
import { isTrialUser } from '@modules/billing-lib/utils'
import { CuiAlert } from '@modules/cui/Alert'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import { isApiErrorCollection } from '@modules/query/helpers'

import { getCreatePayload, getClaimDeploymentRequestPayload } from '@/lib/stackDeployments/crud'
import { getFirstSliderClusterFromGet } from '@/lib/stackDeployments/selectors/fundamentals'
import { kibanaGettingStartedUrl } from '@/lib/serviceProviderDeepLinks'
import { isElasticStaff } from '@/lib/validateEmail'

import ApiRequestExample from '../../../ApiRequestExample'
import SpinButton from '../../../SpinButton'

import type { FC } from 'react'
import type { AllProps as Props } from './types'

const CreateDeploymentButton: FC<Props> = ({
  filterIngestPlugins = false,
  disabled,
  createStackDeploymentRequest,
  fetchStackDeploymentRequest,
  showApiRequest,
  profile,
  resetCreateDeployment,
  isByokToggleEnabled,
  claimInstantStackDeploymentRequest,
  claimInstantStackDeployment,
  renderClaimStackDeploymentError,
  editorState,
  stackVersions,
  region,
  createDeployment: createDeploymentProp,
  redirectToStackGettingStarted,
  fetchProfile,
  isUserconsole,
  isAutoDeleteFeatureEnabled,
  onCreateDeployment,
}) => {
  const navigate = useNavigate()
  useEffect(resetCreateDeployment, [resetCreateDeployment])

  const [, flags] = useFlagsWhenLoaded()

  const [byokError, setByokError] = useState(false)
  const [useAutoDeleteCheckbox, setUseAutoDeleteCheckbox] = useState(true)

  // Check if user is an Elastic employee
  const isElasticEmployee = profile && profile.email ? isElasticStaff(profile.email) : false

  const displayApiRequestLink = !disabled && showApiRequest
  const isTrial = profile && isTrialUser(profile)
  const deploymentRequestProgress =
    createStackDeploymentRequest.inProgress || // creating deployment
    (claimInstantDeploymentProgress() && !createStackDeploymentErrors()) ||
    fetchStackDeploymentRequest.inProgress // fetching claimed deployment

  const { euiTheme } = useEuiTheme()

  return (
    <EuiFlexItem css={{ textAlign: 'center' }} grow={false}>
      <div>
        <EuiFlexGroup alignItems='center' gutterSize='s' direction='column'>
          <EuiFlexItem>
            <SpinButton
              data-test-id='submit-create-deployment'
              css={css({
                [`@media only screen and (max-width: ${euiTheme.breakpoint.m}px)`]: {
                  width: '100%',
                },
              })}
              fill={true}
              disabled={disabled}
              onClick={onClickCreate}
              spin={deploymentRequestProgress}
            >
              <FormattedMessage
                id='create-deployment-from-template.create-deployment'
                defaultMessage='Create hosted deployment'
              />
            </SpinButton>
          </EuiFlexItem>
          {isElasticEmployee && isAutoDeleteFeatureEnabled && (
            <EuiFlexItem>
              <EuiToolTip
                content={
                  <FormattedMessage
                    id='create-deployment-button.auto-delete-tooltip'
                    defaultMessage='When checked, this deployment will be automatically removed after 3 days to help conserve resources.'
                  />
                }
              >
                <EuiCheckbox
                  id='use-default-config-checkbox'
                  label={
                    <FormattedMessage
                      id='create-deployment-button.auto-delete-label'
                      defaultMessage='Deployment will auto-delete'
                    />
                  }
                  checked={useAutoDeleteCheckbox}
                  onChange={(e) => setUseAutoDeleteCheckbox(e.target.checked)}
                  disabled={deploymentRequestProgress || disabled}
                  data-test-id='deployment-default-config-checkbox'
                />
              </EuiToolTip>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>

        {byokError && (
          <Fragment>
            <EuiSpacer size='xs' />
            <CuiAlert type='danger' data-test-id='create-deployment-validation-error'>
              <FormattedMessage
                id='create-deployment-button.error'
                defaultMessage='A key must be provided for encryption at rest'
              />
            </CuiAlert>
          </Fragment>
        )}

        {displayApiRequestLink && !isTrial && (
          <ApiRequestExample
            method='POST'
            endpoint={createDeploymentUrl()}
            body={getCreateDeploymentPayload()}
          />
        )}
      </div>
    </EuiFlexItem>
  )

  function createStackDeploymentErrors() {
    return (
      // fetching claimed deployment
      // creating deployment
      createStackDeploymentRequest.error || fetchStackDeploymentRequest.error
    )
  }

  function claimInstantDeploymentProgress() {
    return (
      // instant deployment not found, switch to create deployment
      // instant deployment claimed
      claimInstantStackDeploymentRequest.inProgress || // claiming instant deployment
      claimInstantStackDeploymentRequest.isDone ||
      getResponseStatus(claimInstantStackDeploymentRequest.error) === 404 ||
      getResponseStatus(claimInstantStackDeploymentRequest.error) === 500
    )
  }

  function onClickCreate() {
    if (profile && isTrialUser(profile) && flags.claimInstantDeployment) {
      claimInstantDeployment()
      return
    }

    createDeployment()
  }

  function getCreateDeploymentPayload(): DeploymentCreateRequest | null {
    if (!region) {
      return null
    }

    const payload = getCreatePayload({
      region,
      editorState,
      filterIngestPlugins,
      stackVersions,
      extraMetadata:
        isElasticEmployee && isAutoDeleteFeatureEnabled && !useAutoDeleteCheckbox
          ? {
              tags: [{ key: 'terminate', value: 'false' }],
            }
          : undefined,
    })

    return payload
  }

  async function claimInstantDeployment() {
    const createDeploymentPayload = getCreateDeploymentPayload()

    if (!createDeploymentPayload) {
      return
    }

    const claimDeploymentRequestPayload = getClaimDeploymentRequestPayload(createDeploymentPayload)

    if (claimDeploymentRequestPayload) {
      try {
        const deployment = await claimInstantStackDeployment(claimDeploymentRequestPayload)
        const kibanaResource = getFirstSliderClusterFromGet({
          deployment,
          sliderInstanceType: `kibana`,
        })
        const discoveryUseCase = profile && getUserUsecase(profile)
        const kibanaLinkUrl = kibanaGettingStartedUrl({
          deploymentBasicInfo: { id: deployment.id, name: deployment.name },
          resource: kibanaResource,
          showGuidedOnboardingPage: flags.guidedOnboarding === true,
          discoveryUseCase: discoveryUseCase ? discoveryUseCase : undefined,
        })

        if (kibanaLinkUrl) {
          navigate(kibanaLinkUrl, { replace: true })
        }
      } catch (err) {
        if (err instanceof AjaxRequestError) {
          // NO standby deployment is available, so create a deployment - we rely on other errors being rendered elsewhere
          if (getResponseStatus(err) === 404) {
            createDeployment()
            return
          }

          renderClaimStackDeploymentError(err)
        }

        if (isApiErrorCollection(err)) {
          if (err.statusCode === 404) {
            createDeployment()
            return
          }
        }
      }
    }
  }

  function validateCreateDeployment(createDeploymentPayload: DeploymentCreateRequest): boolean {
    if (isByokToggleEnabled && createDeploymentPayload.settings?.byok === undefined) {
      setByokError(true)

      return false
    }

    setByokError(false)

    return true
  }

  async function createDeployment() {
    const createDeploymentPayload = getCreateDeploymentPayload()

    if (!createDeploymentPayload || !validateCreateDeployment(createDeploymentPayload)) {
      return
    }

    onCreateDeployment?.(createDeploymentPayload, editorState)

    const actionResult = await createDeploymentProp({
      deployment: createDeploymentPayload,
      profile,
    })

    if (actionResult.error || !actionResult.payload) {
      return // we rely on the error being rendered elsewhere
    }

    if (isUserconsole) {
      fetchProfile()
    }

    const response: DeploymentCreateResponse = actionResult.payload
    const { id } = response

    redirectToStackGettingStarted(id)
  }
}

export default CreateDeploymentButton
